import { jsxs, jsx } from "react/jsx-runtime";
import { forwardRef, useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import { range } from "lodash/fp";
import { startOfWeek, format, endOfWeek, isSameWeek, addDays, isSameMonth } from "date-fns/fp";
import { Calendar } from "../calendar/calendar.es.js";
import { CalendarBody } from "../calendar/calendar-body.es.js";
import { CalendarFooter } from "../calendar/calendar-footer.es.js";
import { CalendarGrid } from "../calendar/calendar-grid.es.js";
import { CalendarHeader } from "../calendar/calendar-header.es.js";
import { CalendarOption } from "../calendar/calendar-option.es.js";
import { CalendarWeekdays } from "../calendar/calendar-weekdays.es.js";
import { UtilityButton } from "../utility-button/utility-button.es.js";
import { WeekOptions, WeekOptionChunk } from "./week-picker.styles.es.js";
import { useWeekPicker } from "./week-picker.hooks.es.js";
import { formatDateRange } from "../../utils/format-date-range.es.js";
const propTypes = {
  inputRef: PropTypes.shape({
    current: PropTypes.instanceOf(Element)
  }),
  maxDate: PropTypes.instanceOf(Date),
  minDate: PropTypes.instanceOf(Date),
  onCancel: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
  onSelectAndClose: PropTypes.func.isRequired,
  value: PropTypes.instanceOf(Date)
};
const defaultProps = {
  inputRef: void 0,
  maxDate: void 0,
  minDate: void 0,
  value: void 0
};
const WeekPicker = forwardRef(
  ({ inputRef, maxDate, minDate, onCancel, onSelect, onSelectAndClose, value }, ref) => {
    const minWeek = useMemo(
      () => minDate ? startOfWeek(minDate) : void 0,
      [minDate]
    );
    const maxWeek = useMemo(
      () => maxDate ? startOfWeek(maxDate) : void 0,
      [maxDate]
    );
    const {
      highlightEndOfMonth,
      highlightNextMonth,
      highlightNextWeek,
      highlightNextYear,
      highlightPreviousMonth,
      highlightPreviousWeek,
      highlightPreviousYear,
      highlightStartOfMonth,
      highlightedDate,
      monthInView,
      setHighlightedDate,
      viewNextMonth,
      viewNextYear,
      viewPreviousMonth,
      viewPreviousYear,
      weeksInView
    } = useWeekPicker({
      initialValue: value,
      maxWeek,
      minWeek
    });
    const handleKeyDown = useCallback(
      (event) => {
        event.stopPropagation();
        switch (event.key) {
          case "ArrowDown":
            highlightNextWeek();
            break;
          case "ArrowLeft":
            highlightPreviousMonth();
            break;
          case "ArrowRight":
            highlightNextMonth();
            break;
          case "ArrowUp":
            highlightPreviousWeek();
            break;
          case "End":
            highlightEndOfMonth();
            break;
          case "Enter":
            if (highlightedDate) {
              onSelectAndClose(highlightedDate);
            }
            break;
          case "Escape":
            onCancel();
            break;
          case "Home":
            highlightStartOfMonth();
            break;
          case "PageDown":
            if (event.shiftKey) {
              highlightNextYear();
            } else {
              highlightNextMonth();
            }
            break;
          case "PageUp":
            if (event.shiftKey) {
              highlightPreviousYear();
            } else {
              highlightPreviousMonth();
            }
            break;
        }
      },
      [
        highlightEndOfMonth,
        highlightNextMonth,
        highlightNextWeek,
        highlightNextYear,
        highlightPreviousMonth,
        highlightPreviousWeek,
        highlightPreviousYear,
        highlightStartOfMonth,
        highlightedDate,
        onCancel,
        onSelectAndClose
      ]
    );
    const handleThisWeekClick = useCallback(() => {
      const thisWeek = startOfWeek(/* @__PURE__ */ new Date());
      setHighlightedDate(thisWeek);
      onSelect(thisWeek);
    }, [onSelect, setHighlightedDate]);
    return /* @__PURE__ */ jsxs(
      Calendar,
      {
        ref,
        inputRef,
        onCancel,
        onKeyDown: handleKeyDown,
        children: [
          /* @__PURE__ */ jsx(
            CalendarHeader,
            {
              onNext: viewNextMonth,
              onPrevious: viewPreviousMonth,
              onSkipBack: viewPreviousYear,
              onSkipForward: viewNextYear,
              children: format("MMM yyyy", monthInView)
            }
          ),
          /* @__PURE__ */ jsxs(CalendarBody, { children: [
            /* @__PURE__ */ jsx(CalendarWeekdays, {}),
            /* @__PURE__ */ jsx(WeekOptions, { role: "listbox", children: weeksInView.map((week) => /* @__PURE__ */ jsx(
              CalendarOption,
              {
                "aria-label": formatDateRange(week, endOfWeek(week), {
                  m: "MMMM",
                  join: " to "
                }),
                highlighted: isSameWeek(week, highlightedDate),
                onHighlight: setHighlightedDate,
                onSelect: onSelectAndClose,
                selected: isSameWeek(week, value),
                value: week,
                children: /* @__PURE__ */ jsx(CalendarGrid, { children: range(0, 7).map((i) => addDays(i, week)).map((date) => /* @__PURE__ */ jsx(
                  WeekOptionChunk,
                  {
                    external: !isSameMonth(monthInView, date) && !isSameWeek(value, week),
                    children: format("d", date)
                  },
                  date.getTime()
                )) })
              },
              week.getTime()
            )) })
          ] }),
          /* @__PURE__ */ jsx(CalendarFooter, { children: /* @__PURE__ */ jsx(UtilityButton, { magnitude: "small", onClick: handleThisWeekClick, children: "This week" }) })
        ]
      }
    );
  }
);
WeekPicker.propTypes = propTypes;
WeekPicker.defaultProps = defaultProps;
export {
  WeekPicker
};
